/** Ranking step — tap items to select them in the correct order with animated reordering. */
import { useMemo, useState, type ReactNode } from 'react';
import { AnimatePresence, motion } from 'motion/react';
import { Check, X, RotateCcw } from 'lucide-react';
import { useSession } from '../SessionContext';
import { AnimatedKbd, useShortcutOverlay } from '../components/ShortcutOverlay';
import { useKeyboardRipple, RippleFlash } from '../components/KeyboardRipple';
import { StepShell } from './StepShell';
import {
  PromptRenderer,
  type TiptapNode,
  type MediaItem,
} from '@/shared/components/PromptRenderer';
import { normaliseMediaRefs } from '../utils/normaliseMediaRefs';
import { FeedbackDisplay } from './FeedbackDisplay';
import type { RankingConfig } from '../types';

interface DiagramJson {
  syntax: string;
  diagramType?: string;
}

type RankingOption = { id: string; text: string };

const EMPTY_OPTIONS: RankingOption[] = [];
const EMPTY_ORDER: string[] = [];

const LAYOUT_SPRING = { type: 'spring' as const, stiffness: 350, damping: 35 };

/**
 * Ranking step — tap items to select them in order.
 * Selected items get numbered badges and glide to the "Your order" section.
 */
export function RankingStep() {
  const { currentStep, state, dispatch } = useSession();
  const { showShortcuts } = useShortcutOverlay();
  const { rippleIndex, rippleKey } = useKeyboardRipple();

  const snapshot = currentStep?.stepSnapshotPublicJson;
  const config = (snapshot?.config ?? null) as unknown as RankingConfig | null;
  const options = config?.options ?? EMPTY_OPTIONS;

  const answer = state.answer?.type === 'ranking' ? state.answer : null;
  const order = answer?.order ?? EMPTY_ORDER;
  const phase = state.phase;
  const result = state.result;
  const rankingResults = result?.rankingResults;
  const isDisabled = phase === 'submitting' || phase === 'grading';
  const isGraded = phase === 'graded';
  const [hoveredId, setHoveredId] = useState<string | null>(null);

  const selectedOptions = useMemo(
    () =>
      order
        .map((id) => options.find((o) => o.id === id))
        .filter((o): o is RankingOption => o != null),
    [order, options]
  );

  const unselectedOptions = useMemo(
    () => options.filter((o) => !order.includes(o.id)),
    [options, order]
  );

  const mediaItems: MediaItem[] | null = normaliseMediaRefs(currentStep?.mediaRefsJson);

  const diagramJson = (currentStep as unknown as Record<string, unknown>)?.diagramJson as
    | DiagramJson
    | null
    | undefined;

  if (!currentStep || !snapshot) return null;

  function handleSelect(optionId: string) {
    if (isDisabled || isGraded) return;
    if (order.includes(optionId)) return;
    dispatch({
      type: 'UPDATE_ANSWER',
      answer: { type: 'ranking', order: [...order, optionId] },
    });
  }

  function handleDeselect(optionId: string) {
    if (isDisabled || isGraded) return;
    dispatch({
      type: 'UPDATE_ANSWER',
      answer: { type: 'ranking', order: order.filter((id) => id !== optionId) },
    });
  }

  function handleReset() {
    if (isDisabled || isGraded) return;
    dispatch({ type: 'UPDATE_ANSWER', answer: { type: 'ranking', order: [] } });
  }

  function getResultForPosition(position: number) {
    if (!rankingResults) return null;
    return rankingResults.find((r) => r.position === position) ?? null;
  }

  // ── Graded view (static, no layout animations) ───────────────────────────

  if (isGraded) {
    return (
      <StepShell>
        <PromptRenderer
          doc={snapshot.prompt as TiptapNode}
          media={mediaItems}
          diagramJson={diagramJson ?? null}
        />
        <div
          style={{
            display: 'flex',
            flexDirection: 'column',
            gap: 'var(--f-space-2)',
            fontFamily: 'var(--f-font-sans)',
          }}
        >
          <span style={{ fontSize: 'var(--f-text-xs)', color: 'var(--f-fg-muted)' }}>
            Your order
          </span>
          {selectedOptions.map((option, index) => {
            const posResult = getResultForPosition(index);
            const isCorrect = posResult?.isCorrect;
            const correctOption = posResult?.correctOptionId
              ? options.find((o) => o.id === posResult.correctOptionId)
              : null;

            return (
              <div key={option.id} style={{ display: 'flex', flexDirection: 'column' }}>
                <div
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 'var(--f-space-3)',
                    padding: 'var(--f-space-3) var(--f-space-4)',
                    borderRadius: 'var(--f-radius-lg)',
                    border: `2px solid ${isCorrect ? 'var(--f-border)' : 'var(--f-error)'}`,
                    backgroundColor: 'var(--f-bg-raised)',
                    color: isCorrect ? 'var(--f-fg)' : 'var(--f-error-text)',
                    fontSize: 'var(--f-text-base)',
                    textAlign: 'left',
                    width: '100%',
                  }}
                >
                  <span
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      width: '1.75rem',
                      height: '1.75rem',
                      borderRadius: 'var(--f-radius-full)',
                      backgroundColor: isCorrect ? 'var(--f-success)' : 'var(--f-error)',
                      color: 'var(--f-fg-on-accent)',
                      fontSize: 'var(--f-text-sm)',
                      fontWeight: 700,
                      flexShrink: 0,
                    }}
                  >
                    {isCorrect ? <Check size={14} /> : <X size={14} />}
                  </span>
                  <span>{option.text}</span>
                </div>
                {!isCorrect && correctOption && (
                  <span
                    style={{
                      fontSize: 'var(--f-text-xs)',
                      color: 'var(--f-success-text)',
                      marginLeft: 'calc(1.75rem + var(--f-space-3))',
                      marginTop: '2px',
                    }}
                  >
                    Correct: {correctOption.text}
                  </span>
                )}
              </div>
            );
          })}
        </div>
        {result && <FeedbackDisplay result={result} />}
      </StepShell>
    );
  }

  // ── Interactive view ──────────────────────────────────────────────────────

  const hasSelected = selectedOptions.length > 0;
  const hasUnselected = unselectedOptions.length > 0;

  // Build a single flat list of keyed elements so AnimatePresence + layout
  // can smoothly handle items gliding between groups and labels entering/exiting.
  const listElements: ReactNode[] = [];

  // "Your order" label
  if (hasSelected) {
    listElements.push(
      <motion.div
        key="label-order"
        layout
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        transition={{ opacity: { duration: 0.15 }, layout: LAYOUT_SPRING }}
        style={{ fontSize: 'var(--f-text-xs)', color: 'var(--f-fg-muted)' }}
      >
        Your order
      </motion.div>
    );
  }

  // Selected items
  selectedOptions.forEach((option, index) => {
    listElements.push(
      <motion.div
        key={option.id}
        layout
        transition={{ layout: LAYOUT_SPRING }}
        style={{ willChange: 'transform' }}
      >
        <button
          type="button"
          onClick={() => handleDeselect(option.id)}
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 'var(--f-space-3)',
            padding: 'var(--f-space-3) var(--f-space-4)',
            borderRadius: 'var(--f-radius-lg)',
            border: '2px solid var(--f-accent)',
            backgroundColor: 'var(--f-accent-subtle)',
            color: 'var(--f-fg)',
            fontSize: 'var(--f-text-base)',
            cursor: 'pointer',
            textAlign: 'left',
            width: '100%',
          }}
        >
          <span
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              width: '1.75rem',
              height: '1.75rem',
              borderRadius: 'var(--f-radius-full)',
              backgroundColor: 'var(--f-accent)',
              color: 'var(--f-fg-on-accent)',
              fontSize: 'var(--f-text-sm)',
              fontWeight: 700,
              flexShrink: 0,
            }}
          >
            {index + 1}
          </span>
          <span>{option.text}</span>
        </button>
      </motion.div>
    );
  });

  // "Tap to add" label
  if (hasSelected && hasUnselected) {
    listElements.push(
      <motion.div
        key="label-add"
        layout
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        transition={{ opacity: { duration: 0.15 }, layout: LAYOUT_SPRING }}
        style={{
          fontSize: 'var(--f-text-xs)',
          color: 'var(--f-fg-muted)',
          paddingTop: 'var(--f-space-2)',
        }}
      >
        Tap to add
      </motion.div>
    );
  }

  // Unselected items
  unselectedOptions.forEach((option) => {
    const originalIndex = options.findIndex((o) => o.id === option.id);
    listElements.push(
      <motion.div
        key={option.id}
        layout
        transition={{ layout: LAYOUT_SPRING }}
        style={{ willChange: 'transform', position: 'relative', overflow: 'visible' }}
      >
        <button
          type="button"
          onClick={() => handleSelect(option.id)}
          onMouseEnter={() => setHoveredId(option.id)}
          onMouseLeave={() => setHoveredId(null)}
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 'var(--f-space-3)',
            padding: 'var(--f-space-3) var(--f-space-4)',
            borderRadius: 'var(--f-radius-lg)',
            border: `2px solid ${hoveredId === option.id ? 'var(--f-accent)' : 'var(--f-border)'}`,
            backgroundColor: 'var(--f-bg-raised)',
            color: 'var(--f-fg)',
            fontSize: 'var(--f-text-base)',
            cursor: 'pointer',
            textAlign: 'left',
            width: '100%',
            transition: 'border-color 0.3s ease',
            outline: 'none',
          }}
        >
          <RippleFlash active={rippleIndex === originalIndex} rippleKey={rippleKey} />
          <AnimatedKbd show={showShortcuts}>{originalIndex + 1}</AnimatedKbd>
          {option.text}
        </button>
      </motion.div>
    );
  });

  // Reset button
  if (order.length > 0) {
    listElements.push(
      <motion.div
        key="reset"
        layout
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        transition={{ opacity: { duration: 0.15 }, layout: LAYOUT_SPRING }}
      >
        <button
          type="button"
          disabled={isDisabled}
          onClick={handleReset}
          className="flex items-center gap-1 rounded-lg px-3 py-1.5 transition-opacity cursor-pointer disabled:opacity-30 disabled:cursor-not-allowed"
          style={{
            fontSize: 'var(--f-text-sm)',
            color: 'var(--f-fg-secondary)',
            fontFamily: 'var(--f-font-sans)',
          }}
        >
          <RotateCcw size={14} />
          Reset
        </button>
      </motion.div>
    );
  }

  return (
    <StepShell>
      <PromptRenderer
        doc={snapshot.prompt as TiptapNode}
        media={mediaItems}
        diagramJson={diagramJson ?? null}
      />

      <div
        style={{
          display: 'flex',
          flexDirection: 'column',
          gap: 'var(--f-space-2)',
          opacity: isDisabled ? 0.7 : 1,
          pointerEvents: isDisabled ? 'none' : 'auto',
          fontFamily: 'var(--f-font-sans)',
        }}
      >
        <AnimatePresence initial={false} mode="popLayout">
          {listElements}
        </AnimatePresence>
      </div>

      {isGraded && result && <FeedbackDisplay result={result} />}
    </StepShell>
  );
}
